import { FC } from 'react';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { SectionHeader } from '../../../common/SectionHeader';
import { Form } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import * as Yup from 'yup';
import { validations } from '../../../../utils/validations';
import { useTranslation } from 'react-i18next';
import { FormContainer } from '../../../common/form/FormContainer';
import { DatePicker } from '../../../common/form/DatePicker';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { Select } from '../../../common/form/Select';
import { QcsMenuItem } from '../../../common/basic/QcsMenuItem';
import { Validator } from '../../../../models/common';
import { getLangNameObj } from '../../../../utils/format';
import { CertificateDto } from '@api/models/suppliers/CertificateDto';

export interface InvitedPersonsDocumentDateModalResult {
  date?: string;
  certificateTypeId?: string;
}

interface InvitedPersonsDocumentDateModalForm {
  date?: string;
  certificateTypeId: string;
}

interface Props {
  open: boolean;
  certificate?: CertificateDto;
  onClose: (result?: InvitedPersonsDocumentDateModalResult) => void;
}

export const InvitedPersonsDocumentDateModal: FC<Props> = ({ open, certificate, onClose }) => {
  const { i18n, t } = useTranslation();

  const dateRequired = !!certificate?.expiryDateRequired;
  const types = certificate?.types ?? [];

  const validator: Validator = {};

  if (dateRequired) {
    validator['date'] = validations.stringDateNotPastRequired(t);
  }
  if (types.length > 0) {
    validator['certificateTypeId'] = validations.stringRequired(t);
  }

  const handleSubmit = (data: InvitedPersonsDocumentDateModalForm) => {
    onClose({
      date: data.date,
      certificateTypeId: data.certificateTypeId || undefined,
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <QcsModal open={open} onClose={handleClose}>
      <QcsModalBox>
        <SectionHeader title="invitation.persons.documentTitle" />
        <FormikDebounce<InvitedPersonsDocumentDateModalForm>
          initialValues={{
            date: undefined,
            certificateTypeId: '',
          }}
          validationSchema={Yup.object().shape(validator)}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              {types.length > 0 && (
                <Select name="certificateTypeId" label={t('invitation.persons.documentType')} required>
                  {types.map((type) => (
                    <QcsMenuItem key={type.id} value={type.id}>
                      {getLangNameObj(i18n, type)}
                    </QcsMenuItem>
                  ))}
                </Select>
              )}
              {dateRequired && <DatePicker name="date" label={t('invitation.persons.documentValidTo')} required={true} disablePast />}
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
